"use client";

import { c as _c } from "react/compiler-runtime";
import { PixelLoader } from "@/components/ui/pixel-loader";
import { cn } from "@/lib/utils";
import { IconAlertWarningOutline18, IconBoltOutline18, IconDatabaseOutline18, IconGearOutline18 } from "nucleo-ui-outline-18";
import { useState } from "react";

// Helper to get fallback icon for connector type (matching ConnectorLogo behavior)
const getIconForConnectorType = (type: string): React.ComponentType<{
  className?: string;
}> => {
  const lowerType = type.toLowerCase();
  if (lowerType.includes("db") || lowerType.includes("database") || lowerType.includes("sql")) {
    return IconDatabaseOutline18;
  }
  if (lowerType.includes("api") || lowerType.includes("webhook") || lowerType.includes("http")) {
    return IconBoltOutline18;
  }
  return IconGearOutline18;
};

/** Shared logo image wrapper used by brand overrides and connector logos */
function LogoImage(t0) {
  const $ = _c(14);
  const {
    src,
    alt,
    className,
    onError,
    showBackground: t1
  } = t0;
  const showBackground = t1 === undefined ? true : t1;
  let t2;
  if ($[0] !== className) {
    t2 = cn("relative", className);
    $[0] = className;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = showBackground ? "dark:bg-ds-neutral-800/80 bg-white/80 backdrop-blur-sm" : "bg-transparent backdrop-blur-0";
  let t4;
  if ($[2] !== t3) {
    t4 = cn("relative flex h-full w-full items-center justify-center overflow-hidden rounded-sm", t3);
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== alt || $[5] !== onError || $[6] !== src) {
    t5 = <img src={src} alt={alt} loading="lazy" className="h-full w-full object-contain" onError={onError} />;
    $[4] = alt;
    $[5] = onError;
    $[6] = src;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t4 || $[9] !== t5) {
    t6 = <div className={t4}>{t5}</div>;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t2 || $[12] !== t6) {
    t7 = <div className={t2}>{t6}</div>;
    $[11] = t2;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
}
interface NodeIconProps {
  /**
   * Unique identifier for the icon (used for caching)
   */
  id: string;

  /**
   * Subject/title of what the icon represents
   */
  subject: string;

  /**
   * Context/description for the AI to understand what to generate
   */
  context: string;

  /**
   * Optional custom logo URL - if provided, this will be used instead of AI generation
   */
  logoUrl?: string;

  /**
   * Optional connector type for fallback logo (e.g., "pipedream:gmail")
   * If provided, will try to load connector logo from logo.dev when AI generation fails
   */
  connectorType?: string;

  /**
   * Optional connector info - if provided with a single connector,
   * the connector logo will be shown instead of the AI-generated icon
   */
  connector?: {
    type: string;
    logoUrl?: string;
  };

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Alt text for accessibility
   */
  alt?: string;

  /**
   * Icon color (CSS color value) - uses currentColor in SVG
   */
  color?: string;

  /**
   * Template to use for generation (default: b3os-app-flat)
   */
  template?: "line-icon" | "moebius-icon" | "b3os-app-flat";

  /**
   * Format to use (svg or png, default: svg)
   */
  format?: "svg" | "png";

  /**
   * Whether to show the built-in icon background shell
   */
  showBackground?: boolean;
}
import { ICON_API } from "@/constants/urls";

/**
 * Icon version for cache busting
 * Increment this number to force regeneration of all icons across the app
 */
const ICON_VERSION = 9;
export function NodeIcon({
  id,
  subject,
  context = "crypto workflow automation",
  logoUrl,
  connectorType,
  connector,
  className,
  alt,
  color = "white",
  template = "b3os-app-flat",
  format = "svg",
  showBackground = true
}: NodeIconProps) {
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);
  const [connectorFallbackUrl, setConnectorFallbackUrl] = useState<string | null>(null);
  const [connectorFallbackFailed, setConnectorFallbackFailed] = useState(false);
  const [connectorLogoStage, setConnectorLogoStage] = useState(0);
  const [brandLogoError, setBrandLogoError] = useState(false);

  // Compute template-specific icon key
  const iconKey = `${id}-${template}`;

  // If an explicit logoUrl is provided by the definition, show it directly.
  if (logoUrl && !brandLogoError) {
    return <LogoImage src={logoUrl} alt={alt || subject} className={className} onError={() => setBrandLogoError(true)} showBackground={showBackground} />;
  }

  // If a connector is provided, show the connector logo instead of AI-generated icon
  if (connector) {
    const brandName = connector.type.includes(":") ? connector.type.split(":")[1] : connector.type;
    const aiLogoUrl = `https://ai-icon.b3.fun/logo/${encodeURIComponent(brandName.replace(/\s+/g, "_"))}`;
    const IconComponent = getIconForConnectorType(connector.type);

    // Build ordered, deduplicated source list (logoUrl first, matching ConnectorLogo.tsx)
    const rawSources = [connector.logoUrl, aiLogoUrl].filter(Boolean) as string[];
    const connectorSources = rawSources.filter((url, i) => rawSources.indexOf(url) === i);
    const connectorLogoSrc = connectorSources[connectorLogoStage] ?? null;

    // Show fallback icon if all sources exhausted
    if (!connectorLogoSrc) {
      return <div className={cn("relative", className)}>
          <div className={cn("flex h-full w-full items-center justify-center rounded-sm", showBackground ? "bg-blue-100/80 backdrop-blur-sm dark:bg-blue-500/20" : "bg-transparent backdrop-blur-0")}>
            <IconComponent className="h-1/2 w-1/2 text-blue-600 dark:text-blue-400" />
          </div>
        </div>;
    }

    // Show connector logo
    return <LogoImage src={connectorLogoSrc} alt={alt || subject} className={className} onError={() => setConnectorLogoStage(s => s + 1)} showBackground={showBackground} />;
  }

  // Compute connector fallback URL if we have a connector type
  const getConnectorLogoUrl = (): string | null => {
    if (!connectorType) return null;
    // For connector types like "pipedream:gmail", extract the app name
    const brandName_0 = connectorType.includes(":") ? connectorType.split(":")[1] : connectorType;
    return `https://ai-icon.b3.fun/logo/${encodeURIComponent(brandName_0)}`;
  };

  // Compute icon URL directly from props (no useEffect needed!)
  // Priority: logoUrl -> generatedUrl -> connectorFallbackUrl -> AI icon API
  const iconUrl = logoUrl || generatedUrl || connectorFallbackUrl || `${ICON_API}/?id=${id}&format=${format}&template=${template}&v=${ICON_VERSION}`;

  // Determine if we should use mask (for monochrome icons with custom colors)
  // or regular img (for icons with their own colors like b3os-app-flat / moebius)
  // Don't use mask for connector fallback images (they have their own colors)
  const useMask = color && template === "line-icon" && !logoUrl && !connectorFallbackUrl;
  const handleImageError = () => {
    // If we're showing the connector fallback and it failed, show error state
    if (connectorFallbackUrl) {
      setConnectorFallbackFailed(true);
      setHasError(true);
      return;
    }

    // Icon doesn't exist or failed to load - trigger generation
    if (!isGenerating && !hasError) {
      generateIcon();
    }
  };
  const generateIcon = async () => {
    setIsGenerating(true);
    try {
      // Trigger generation via POST
      const response = await fetch(`${ICON_API}/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          id,
          subject,
          context,
          template
        })
      });
      if (response.status === 409) {
        // Already generating, poll for completion
        pollStatus();
        return;
      }
      if (!response.ok) {
        throw new Error("Generation failed");
      }

      // Poll for completion
      pollStatus();
    } catch (err) {
      console.error("Icon generation failed:", err);
      // Try connector fallback before giving up
      const fallbackUrl = getConnectorLogoUrl();
      if (fallbackUrl && !connectorFallbackFailed) {
        setConnectorFallbackUrl(fallbackUrl);
        setIsGenerating(false);
        setIsLoading(true);
      } else {
        setHasError(true);
        setIsGenerating(false);
      }
    }
  };
  const pollStatus = async () => {
    let attempts = 0;
    const maxAttempts = 60; // 3 minutes

    const interval = setInterval(async () => {
      attempts++;
      try {
        const response_0 = await fetch(`${ICON_API}/status/${iconKey}`);
        const status = await response_0.json();
        if (status.status === "complete") {
          clearInterval(interval);
          // Update to generated URL (triggers reload)
          const newUrl = `${ICON_API}/?id=${id}&format=${format}&template=${template}&v=${ICON_VERSION}&t=${Date.now()}`;
          setGeneratedUrl(newUrl);
          setIsGenerating(false);
          setIsLoading(false);
        } else if (status.status === "errored" || status.status === "not_found") {
          clearInterval(interval);
          // Try connector fallback before giving up
          const fallbackUrl_0 = getConnectorLogoUrl();
          if (fallbackUrl_0 && !connectorFallbackFailed) {
            setConnectorFallbackUrl(fallbackUrl_0);
            setIsGenerating(false);
            setIsLoading(true);
          } else {
            setHasError(true);
            setIsGenerating(false);
          }
        }
        if (attempts >= maxAttempts) {
          clearInterval(interval);
          // Try connector fallback before giving up
          const fallbackUrl_1 = getConnectorLogoUrl();
          if (fallbackUrl_1 && !connectorFallbackFailed) {
            setConnectorFallbackUrl(fallbackUrl_1);
            setIsGenerating(false);
            setIsLoading(true);
          } else {
            setHasError(true);
            setIsGenerating(false);
          }
        }
      } catch (err_0) {
        console.error("Status poll error:", err_0);
      }
    }, 3000);
  };

  // Fallback to default icon if everything fails
  if (hasError) {
    return <div className={cn("relative", className)}>
        <div className={cn("relative flex aspect-square h-full w-full flex-shrink-0 flex-col items-center justify-center gap-2 rounded-lg", showBackground ? "bg-ds-neutral-100/80 dark:bg-ds-neutral-800/80 backdrop-blur-sm" : "bg-transparent backdrop-blur-0")}>
          <IconAlertWarningOutline18 className="text-ds-text-disabled dark:text-ds-text-subtle h-4 w-4" />
        </div>
      </div>;
  }
  return <div className={cn("relative", className)}>
      {/* Loading/Generating state */}
      {(isLoading || isGenerating) && <div className={cn("relative flex h-full w-full flex-col items-center justify-center gap-2 rounded-lg", showBackground ? "bg-ds-neutral-100/80 dark:bg-ds-neutral-800/80 backdrop-blur-sm" : "bg-transparent backdrop-blur-0")}>
          <PixelLoader size="sm" color="blue" />
        </div>}
      {/* Icon rendering - mask for monochrome, img for colored */}
      {iconUrl && <>
          {useMask ?
      // Mask approach for monochrome icons (line-icon)
      <div className={cn("h-full w-full rounded-lg transition-opacity", isLoading || isGenerating ? "opacity-0" : "opacity-100")} style={{
        maskImage: `url(${iconUrl})`,
        WebkitMaskImage: `url(${iconUrl})`,
        maskSize: "contain",
        WebkitMaskSize: "contain",
        maskRepeat: "no-repeat",
        WebkitMaskRepeat: "no-repeat",
        maskPosition: "center",
        WebkitMaskPosition: "center",
        backgroundColor: color
      }} role="img" aria-label={alt || subject}>
              {/* Hidden img to trigger loading events */}
              <img src={iconUrl} alt="" className="invisible h-0 w-0" onLoad={() => setIsLoading(false)} onError={handleImageError} />
            </div> :
      // Regular img for colored icons (b3os-app-flat, moebius-icon)
      <img src={iconUrl} alt={alt || subject} loading="lazy" className={cn("h-full w-full rounded-lg object-contain transition-opacity", isLoading || isGenerating ? "opacity-0" : "opacity-100")} onLoad={() => setIsLoading(false)} onError={handleImageError} />}
        </>}
    </div>;
}